/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import { Modal as AntdModal } from "antd";
import { ModalProps } from "antd/lib/modal";
import React, { useMemo } from "react";
import Button from "src/components/Button";
import { useFormatMessage, useTheme } from "src/hooks";
import messageIds from "src/locales/messageIds";
import { reactMemo } from "src/utils/react";

export type ModalSizeType = "small" | "large";
export const MODAL_BODY_MAXHEIGHT = "665px";

const Footer = reactMemo(function Footer(
  props: Pick<ModalProps, "okText" | "onOk" | "cancelText" | "onCancel"> & {
    cancelVisible?: boolean;
    okDisabled?: boolean;
    description?: string;
  }
) {
  const {
    description,
    okText,
    onOk,
    cancelText,
    onCancel,
    okDisabled,
    cancelVisible = true,
  } = props;
  const formatMessage = useFormatMessage();
  const theme = useTheme();

  return (
    <div
      css={css`
        display: flex;
        align-items: center;
        text-align: left;
        button {
          margin-right: auto;
          min-width: 88px;
          height: 32px;
        }
      `}
    >
      <div
        css={css`
          flex-grow: 1;
          font-size: 12px;
          color: ${theme.infoText};
        `}
      >
        {description}
      </div>

      {cancelVisible && (
        <Button onClick={onCancel}>
          {cancelText || formatMessage(messageIds.common.cancel)}
        </Button>
      )}
      <Button type="primary" onClick={onOk} disabled={okDisabled}>
        {okText || formatMessage(messageIds.common.confirm)}
      </Button>
    </div>
  );
});

export default React.memo(function Modal(
  props: ModalProps & {
    description?: string;
    children: React.ReactNode;
    size?: ModalSizeType;
    cancelVisible?: boolean;
    okDisabled?: boolean;
    onClose?: () => void;
  }
) {
  const {
    description,
    title,
    width,
    size,
    okText,
    onOk,
    cancelText,
    onCancel,
    onClose,
    destroyOnClose = true,
    maskClosable = false,
    children,
    cancelVisible,
    okDisabled,
    ...restProps
  } = props;
  let { footer } = props;

  if (footer === undefined) {
    footer = (
      <Footer
        okText={okText}
        description={description}
        okDisabled={okDisabled}
        cancelText={cancelText}
        cancelVisible={cancelVisible}
        onOk={onOk}
        onCancel={onCancel}
      />
    );
  }

  const modalWidth = useMemo(() => {
    const defaultWidth = 440;
    if (width) {
      return width;
    }
    switch (size) {
      case "small":
        return defaultWidth;
      case "large":
        return 800;
      default:
        return defaultWidth;
    }
  }, [width, size]);

  return (
    <AntdModal
      title={title}
      width={modalWidth}
      footer={footer}
      onCancel={onClose ?? onCancel}
      destroyOnClose={destroyOnClose}
      maskClosable={maskClosable}
      {...restProps}
      css={css`
        .ant-modal-header {
          border-bottom: 0;
          padding: 30px;
          .ant-modal-title {
            font-weight: 600;
          }
        }
        .ant-modal-body {
          padding: 0 30px;
          max-height: ${footer ? MODAL_BODY_MAXHEIGHT : "722px"};
          overflow-y: auto;
        }
        .ant-modal-footer {
          border-top: 0;
          padding: 30px;
        }
        .ant-modal-footer button + button {
          margin-left: 20px;
        }
      `}
    >
      {children}
    </AntdModal>
  );
});
